import { useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import ReCAPTCHA from "react-google-recaptcha";

const recaptchaSiteKey = import.meta.env.VITE_GOOGLE_CAPTCHA_KEY;

// API URL setup
let apiUrl = "https://api.gimmefy.ai/v2";
const baseUrl = window.location.origin;
if (baseUrl.includes("localhost") || baseUrl.includes("127.0.0.1") || baseUrl.includes("dev.gimmefy.ai")) {
  apiUrl = "https://apidev.gimmefy.ai/v2";
} else if (baseUrl.includes("staging.gimmefy.ai")) {
  apiUrl = "https://apistaging.gimmefy.ai/v2";
}

interface NotifyFormData {
  fullName: string;
  companyJobTitle: string;
  email: string;
  linkedinProfile?: string;
}

export const PromptAndPlateNotifyForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [captchaValue, setCaptchaValue] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<NotifyFormData>();

  // Handle reCAPTCHA change
  const handleCaptchaChange = (value: string | null) => {
    setCaptchaValue(value);
  };

  const onSubmit = async (data: NotifyFormData) => {
    // Check if reCAPTCHA is completed
    if (!captchaValue) {
      toast.error("Please complete the reCAPTCHA verification");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        event_name: "PROMPT_AND_PLATE_CANT_MAKE_SESSION_NOTIFICATION",
        payload: {
          full_name: data.fullName,
          company_job_title: data.companyJobTitle,
          email: data.email,
          linkedin_profile: data.linkedinProfile || null,
          timestamp: new Date().toISOString()
        },
        captcha_code: captchaValue
      };

      const response = await fetch(`${apiUrl}/triggerNotificationPublic`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Failed to send notification');
      }

      toast.success("Thank you! We'll notify you about the next Prompt & Plate session.");
      reset();
      setCaptchaValue(null);
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
      console.error("Form submission error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 bg-card p-8 rounded-lg border">
      {/* Full Name */}
      <div className="space-y-2">
        <Label htmlFor="fullName">
          Full name <span className="text-destructive">*</span>
        </Label>
        <Input
          id="fullName"
          {...register("fullName", {
            required: "Full name is required",
            maxLength: {
              value: 100,
              message: "Name must be less than 100 characters",
            },
          })}
          placeholder="Your full name"
        />
        {errors.fullName && (
          <p className="text-sm text-destructive">{errors.fullName.message}</p>
        )}
      </div>

      {/* Company and Job Title */}
      <div className="space-y-2">
        <Label htmlFor="companyJobTitle">
          Company and job title <span className="text-destructive">*</span>
        </Label>
        <Input
          id="companyJobTitle"
          {...register("companyJobTitle", {
            required: "Company and job title are required",
            maxLength: {
              value: 150,
              message: "Company and job title must be less than 150 characters",
            },
          })}
          placeholder="e.g., Marketing Director at Acme Corp"
        />
        {errors.companyJobTitle && (
          <p className="text-sm text-destructive">{errors.companyJobTitle.message}</p>
        )}
      </div>

      {/* Email */}
      <div className="space-y-2">
        <Label htmlFor="email">
          Email address <span className="text-destructive">*</span>
        </Label>
        <Input
          id="email"
          type="email"
          {...register("email", {
            required: "Email is required",
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: "Invalid email address",
            },
            maxLength: {
              value: 255,
              message: "Email must be less than 255 characters",
            },
          })}
          placeholder="your.email@company.com"
        />
        {errors.email && (
          <p className="text-sm text-destructive">{errors.email.message}</p>
        )}
      </div>

      {/* LinkedIn Profile */}
      <div className="space-y-2">
        <Label htmlFor="linkedinProfile">
          LinkedIn profile{" "}
          <span className="text-muted-foreground">(optional)</span>
        </Label>
        <Input
          id="linkedinProfile"
          type="url"
          {...register("linkedinProfile", {
            pattern: {
              value: /^https?:\/\/(www\.)?linkedin\.com\/.*$/,
              message: "Please enter a valid LinkedIn URL",
            },
          })}
          placeholder="https://linkedin.com/in/yourprofile"
        />
        {errors.linkedinProfile && (
          <p className="text-sm text-destructive">{errors.linkedinProfile.message}</p>
        )}
      </div>

      {/* reCAPTCHA */}
      <div className="space-y-2">
        <ReCAPTCHA
          id="prompt-plate-notify-recaptcha"
          ref={recaptchaRef}
          sitekey={recaptchaSiteKey}
          onChange={handleCaptchaChange}
          theme="light"
        />
        {!captchaValue && (
          <p className="text-sm text-muted-foreground mt-2">
            Please complete the reCAPTCHA verification above
          </p>
        )}
      </div>

      {/* Submit Button */}
      <div className="space-y-4">
        <Button
          type="submit"
          size="lg"
          className="w-full"
          disabled={isSubmitting || !captchaValue}
        >
          {isSubmitting ? "Submitting..." : "Keep me updated"}
        </Button>

        <p className="text-sm text-muted-foreground text-center">
          We will only use your details to notify you about future Prompt and Plate sessions and to manage event logistics. We do not share or sell your data. Submissions are requests to be informed and are not guaranteed bookings.
        </p>
      </div>
    </form>
  );
};
